import { useCallback, useMemo, useState } from "react";

import { getCashFlow } from "../../../../Api/apiClient";
import { CURRENCIES } from "../../balanceSheetConstants";
import {
  calculateDateRange,
  getRangeDisplayLabel,
  getRangeSelectionLabel,
} from "../../Shared/Utils/dateRanges";
import { exportCashFlowToCSV, exportCashFlowToPDF } from "../utils/exportUtils";
import { transformCashFlowApiData } from "../utils/reportUtils";

export default function useCashFlowData(texts, toast) {
  const { fromDate, toDate } = useMemo(
    () => calculateDateRange("tq"),
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [currentRangeKey, setCurrentRangeKey] = useState("tq");
  const [filters, setFilters] = useState({
    date_after: fromDate,
    date_before: toDate,
    currency: "SAR",
    period_count: 0,
    group_by: "",
    branches: [],
    costCenters: [],
    projects: [],
    contacts: [],
  });
  const dateRangeLabel = useMemo(
    () =>
      currentRangeKey === "custom"
        ? getRangeSelectionLabel("custom", texts, filters.date_after, filters.date_before)
        : getRangeDisplayLabel(currentRangeKey, texts),
    [currentRangeKey, filters.date_after, filters.date_before, texts]
  );

  const fetchReport = useCallback(
    async (currentFilters) => {
      setIsLoading(true);
      try {
        const response = await getCashFlow({
          date_after: currentFilters.date_after,
          date_before: currentFilters.date_before,
          currency: currentFilters.currency,
          group_by: currentFilters.group_by,
          branches: (currentFilters.branches || []).join(","),
          cost_center__in: (currentFilters.costCenters || []).join(","),
          projects: (currentFilters.projects || []).join(","),
          contacts: (currentFilters.contacts || []).join(","),
          activity: "all",
          level: "group",
        });
        setReportData(response.data);
      } catch (error) {
        setReportData({ columns: [], rows: [] });
        toast({
          title: texts.reports?.error || "Error",
          description:
            error.response?.data?.errors?.[0]?.detail ||
            error.message ||
            texts.reports?.couldNotLoadReport ||
            "Could not load Cash Flow report data.",
          status: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [texts, toast]
  );

  const transformedData = useMemo(
    () => transformCashFlowApiData(reportData),
    [reportData]
  );

  const isReportEmpty = useMemo(
    () => !isLoading && (!reportData || reportData.rows?.length === 0),
    [isLoading, reportData]
  );

  const updateFilters = useCallback(
    async (nextFilters) => {
      setFilters(nextFilters);
      await fetchReport(nextFilters);
    },
    [fetchReport]
  );

  const handleSelectRange = useCallback(
    (range) => {
      const { fromDate: nextFromDate, toDate: nextToDate } = calculateDateRange(range);
      setCurrentRangeKey(range);
      void updateFilters({
        ...filters,
        date_after: nextFromDate,
        date_before: nextToDate,
      });
    },
    [filters, updateFilters]
  );

  const handleApplyCustomRange = useCallback(
    ({ from_date: fromDate, to_date: toDate }) => {
      setCurrentRangeKey("custom");
      void updateFilters({
        ...filters,
        date_after: fromDate,
        date_before: toDate,
      });
    },
    [filters, updateFilters]
  );

  const handleApplyFilters = useCallback(
    (nextFilters) => {
      void updateFilters(nextFilters);
    },
    [updateFilters]
  );

  const handleCurrencyChange = useCallback(
    (currency) => {
      void updateFilters({
        ...filters,
        currency,
      });
    },
    [filters, updateFilters]
  );

  const handleExport = useCallback(
    async (format) => {
      setIsExporting(true);
      try {
        if (format === "PDF") {
          exportCashFlowToPDF(transformedData, filters, toast, texts, CURRENCIES);
        } else {
          exportCashFlowToCSV(transformedData, filters, toast, texts);
        }
      } finally {
        setIsExporting(false);
      }
    },
    [filters, texts, toast, transformedData]
  );

  return {
    dateRangeLabel,
    filters,
    isExporting,
    isLoading,
    isReportEmpty,
    transformedData,
    fetchReport,
    handleApplyCustomRange,
    handleApplyFilters,
    handleCurrencyChange,
    handleExport,
    handleSelectRange,
  };
}
